import type { FormFieldDto, ManagedFragmentDto } from '../../general/dtos';

export const LAYOUT_WIDTHS = [360, 390, 768, 1366] as const;
export type LayoutSource = { html: string; theme: string; fragmentKey: string; fields: FormFieldDto[]; fragments: ManagedFragmentDto[] };
export type LayoutFragmentSource = LayoutSource & { title: string; order: number; hidden?: boolean };
export type LayoutFinding = { kind: 'page-overflow' | 'oversized-element' | 'clipped-content'; selector: string; title: string; detail: string; element: string; suggestion?: string; sourceId?: string; tagName?: string; clippedY?: boolean };
export type LayoutResult = { width: number; findings: LayoutFinding[]; notes: string[]; error?: string };

export function moduleLayoutSources(fragments: ManagedFragmentDto[], currentKey: string | null, currentHtml: string, theme: string): LayoutFragmentSource[] {
    const snapshot = fragments.map(fragment => fragment.key === currentKey ? { ...fragment, html: currentHtml } : fragment);
    return [...snapshot].sort((a, b) => a.order - b.order).map(fragment => ({
        html: fragment.html ?? '', theme, fragmentKey: fragment.key, title: fragment.title, order: fragment.order, hidden: !!fragment.hidden,
        fields: fragment.formFields ?? [], fragments: snapshot,
    }));
}

/** Serialize the shared module context once, rather than repeating it for each fragment. */
export function layoutSourcesSignature(sources: LayoutFragmentSource[]): string {
    return JSON.stringify({ fragments: sources[0]?.fragments ?? [], sources: sources.map(({ fragments, ...source }) => source) });
}

function selectorFor(element: Element): string {
    if (element.tagName === 'BODY') return 'body';
    const parts: string[] = [];
    let node: Element | null = element;
    while (node && node.tagName !== 'BODY') {
        const parent: Element | null = node.parentElement;
        if (!parent) return 'body';
        parts.unshift(`:nth-child(${Array.from(parent.children).indexOf(node) + 1})`);
        node = parent;
    }
    return `body > ${parts.join(' > ')}`;
}

function isImage(element: HTMLElement): boolean {
    return element.tagName === 'IMG' || (element.matches('figure.image-block') && !!element.querySelector('img'));
}

function tableFor(element: HTMLElement): HTMLElement | null {
    if (element.tagName === 'TABLE') return element;
    const table = element.querySelector<HTMLElement>(':scope > table');
    if (!table) return null;
    return element.matches('.lesson-table-responsive, .tableWrapper, figure')
        || (element.children.length === 1 && table.hasAttribute('data-layout-source')
            && !Array.from(element.childNodes).some(node => node.nodeType === 3 && node.textContent?.trim()))
        ? table : null;
}

function describe(element: HTMLElement): string {
    const name = tableFor(element) ? 'Table' : isImage(element) ? 'Image' : ({ IFRAME: 'Embed', VIDEO: 'Video', INPUT: 'Answer field', SELECT: 'Answer field', TEXTAREA: 'Answer field' } as Record<string, string>)[element.tagName] ?? 'Block';
    const text = (element.getAttribute('alt') || element.getAttribute('aria-label') || (isImage(element) && element.querySelector('img')?.getAttribute('alt')) || element.textContent || '').replace(/\s+/g, ' ').trim();
    return text ? `${name}: ${text.slice(0, 70)}${text.length > 70 ? '…' : ''}` : name;
}

/** Inspect layout only; never change the authored DOM or inline styles. */
export function inspectLayout(doc: Document): LayoutFinding[] {
    const win = doc.defaultView;
    if (!win || !doc.body) return [];
    const width = doc.documentElement.clientWidth || win.innerWidth;
    const tolerance = 3;
    const findings: LayoutFinding[] = [];
    if (Math.max(doc.documentElement.scrollWidth, doc.body.scrollWidth) > width + tolerance) findings.push({
        kind: 'page-overflow', selector: 'body', element: 'Lesson page', title: 'Page scrolls sideways',
        detail: `Content is ${Math.round(Math.max(doc.documentElement.scrollWidth, doc.body.scrollWidth))}px wide in a ${width}px viewport. Review the elements below.`,
        suggestion: 'Review the oversized elements listed at this width. Reduce fixed widths, allow columns to stack, or keep wide tables inside their own horizontally scrollable container.'
    });
    const reported = new Set<Element>();
    const visible = (element: Element) => {
        for (let node: Element | null = element; node && node !== doc.body; node = node.parentElement) {
            const style = win.getComputedStyle(node);
            if (style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse') return false;
        }
        return element.getClientRects().length > 0;
    };
    for (const element of Array.from(doc.body.querySelectorAll<HTMLElement>('*'))) {
        if (element.namespaceURI !== 'http://www.w3.org/1999/xhtml'
            || /^(SCRIPT|STYLE|LINK|BR|OPTION)$/.test(element.tagName)
            || element.closest('#lesson-preview-watermark, .graph-viewport, .collapsible.collapsed .collapsible-body') || !visible(element)) continue;
        const rect = element.getBoundingClientRect();
        if (!rect.width || !rect.height) continue;
        const style = win.getComputedStyle(element);
        const table = tableFor(element);
        const source = { sourceId: (table ?? element).closest('[data-layout-source]')?.getAttribute('data-layout-source') ?? undefined, tagName: element.tagName };
        // Avoid repeating an oversized container's issue for every descendant.
        let suppressed = false;
        for (let parent = element.parentElement; parent && parent !== doc.body; parent = parent.parentElement) {
            const parentStyle = win.getComputedStyle(parent);
            if (reported.has(parent) || /^(auto|scroll|hidden|clip)$/.test(parentStyle.overflowX)) { suppressed = true; break; }
        }
        const clippedX = /^(hidden|clip)$/.test(style.overflowX) && element.scrollWidth > element.clientWidth + tolerance;
        const clippedY = /^(hidden|clip)$/.test(style.overflowY) && element.scrollHeight > element.clientHeight + tolerance;
        const fixed = [element.style.width && `width ${element.style.width}`, element.style.height && `height ${element.style.height}`].filter(Boolean).join(', ');
        if ((clippedX || clippedY) && !suppressed) {
            findings.push({ ...source, clippedY, kind: 'clipped-content', selector: selectorFor(element), element: describe(element), title: 'Content may be clipped',
                detail: `${clippedX && clippedY ? 'Horizontal and vertical content exceeds' : clippedX ? 'Content is wider than' : 'Content is taller than'} a container that hides overflow.${fixed ? ` Authored sizing: ${fixed}.` : ''}`,
                suggestion: clippedY
                    ? 'If all this content should be visible, remove or increase the fixed height and let the block grow with its content. If the height must stay fixed, use scrolling instead of hiding overflow.'
                    : 'If all this content should be visible, reduce its width or allow text and columns to wrap. For intentionally wide content, use a scrollable container instead of hiding overflow.' });
            reported.add(element);
            continue;
        }
        if (suppressed) continue;
        const parent = element.parentElement;
        const bounds = parent?.getBoundingClientRect();
        const outsideBounds = rect.left < -tolerance || rect.right > width + tolerance
            || (bounds && (rect.left < bounds.left - tolerance || rect.right > bounds.right + tolerance));
        const overflowingContent = style.overflowX === 'visible' && element.clientWidth > 0 && element.scrollWidth > element.clientWidth + tolerance;
        // A wide table can increase every ancestor's scrollWidth. Inspect the
        // table itself rather than reporting a fitting layout block and hiding it.
        if (!outsideBounds && overflowingContent && !table && element.querySelector('table')) continue;
        const outside = outsideBounds || overflowingContent;
        if (outside) {
            findings.push({ ...source, kind: 'oversized-element', selector: selectorFor(element), element: describe(element), title: 'Element extends beyond its container',
                detail: `${Math.round(rect.width)}px-wide ${element.tagName.toLowerCase()} extends beyond the available space.${fixed ? ` Authored sizing: ${fixed}.` : ''}`,
                suggestion: isImage(element)
                    ? 'Fit the image and its figure wrapper to the available width, limit horizontal margins, and use max-width: 100% and height: auto on the image so it fits without distortion.'
                    : table
                        ? 'Reduce fixed table or column widths and allow cell text to wrap. If the table must remain wide, place it inside a container with overflow-x: auto.'
                        : /^(INPUT|SELECT|TEXTAREA)$/.test(element.tagName)
                            ? 'Reduce the answer field width or set max-width: 100%. If it sits beside a label, allow the label and field to stack on narrow screens.'
                            : /^(IFRAME|VIDEO)$/.test(element.tagName)
                                ? 'Reduce the media width or set max-width: 100%. Adjust its height or aspect ratio as well so it keeps the intended proportions.'
                                : 'Reduce fixed widths or minimum widths, allow text to wrap, and let side-by-side blocks stack on narrow screens. Check large margins or positioning if the block is shifted outside its container.' });
            reported.add(element);
        }
    }
    return findings;
}

/** Bounded loading: incomplete resources must be reported, never mistaken for a clean result. */
export async function waitForLayout(doc: Document, signal: AbortSignal, timeout = 7000): Promise<string[]> {
    const notes = new Set<string>();
    const cleanups: (() => void)[] = [];
    const waitFor = (element: Element, label: string) => new Promise<void>(resolve => {
        const loaded = () => resolve();
        const failed = () => { notes.add(`${label} could not load; results may be incomplete.`); resolve(); };
        element.addEventListener('load', loaded, { once: true });
        element.addEventListener('error', failed, { once: true });
        cleanups.push(() => { element.removeEventListener('load', loaded); element.removeEventListener('error', failed); });
    });
    const styles = Array.from(doc.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"][href]'))
        .filter(link => !!link.getAttribute('href') && !link.sheet).map(link => waitFor(link, 'A stylesheet'));
    const images = Array.from(doc.images).filter(image => !!image.getAttribute('src')).map(image => {
        if (!image.complete) return waitFor(image, 'An image');
        if (!image.naturalWidth) notes.add('An image could not load; results may be incomplete.');
        return Promise.resolve();
    });
    let timer: ReturnType<typeof setTimeout>;
    let abort: () => void = () => {};
    const cancelled = new Promise<void>(resolve => {
        abort = () => resolve();
        if (signal.aborted) resolve(); else signal.addEventListener('abort', abort, { once: true });
    });
    try {
        await Promise.race([
            Promise.all([...styles, ...images]).then(async () => {
                await doc.fonts?.ready;
                // Let responsive styles and image/font dimensions settle.
                await new Promise(resolve => setTimeout(resolve, 80));
            }),
            new Promise<void>(resolve => { timer = setTimeout(() => { notes.add('Some resources did not finish loading in time; results may be incomplete.'); resolve(); }, timeout); }),
            cancelled
        ]);
    } finally {
        clearTimeout(timer!);
        signal.removeEventListener('abort', abort);
        cleanups.forEach(cleanup => cleanup());
    }
    return [...notes];
}

export function highlightFinding(doc: Document, finding: LayoutFinding): () => void {
    const element = doc.querySelector<HTMLElement>(finding.selector);
    if (!element) return () => {};
    const original = element.style.getPropertyValue('outline');
    const priority = element.style.getPropertyPriority('outline');
    element.style.setProperty('outline', '3px solid #e8790b', 'important');
    const top = element.getBoundingClientRect().top + (doc.defaultView?.scrollY ?? 0);
    doc.defaultView?.scrollTo({ top: Math.max(0, top - 32), left: 0, behavior: 'instant' as ScrollBehavior });
    return () => {
        if (original) element.style.setProperty('outline', original, priority); else element.style.removeProperty('outline');
    };
}
